import {
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import t from "@/lang/lang";
import { ChevronLeft, ChevronRight, RefreshCcw } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { Button } from "@/shared/components/ui/button";
import { MonitorTypeUtil } from "@/services/backend/qpm/lib/monitor-type-util";
import type { CatalogItemRes } from "@/services/backend/qpm/catalog/types/catalog";
import { MonitorType } from "@/services/backend/qpm/types/enums";
import type { PrevNextNav } from "@/routes/base-data/processes/qpm/monitors/[monitorId]/_components/monitor-component";
import { CatalogReleaseRefreshControls } from "@/routes/base-data/processes/qpm/catalog/_components/catalog-release-refresh-controls";
import { CatalogMonitorPestsComponent } from "@/routes/base-data/processes/qpm/catalog/[catalogId]/[monitorId]/_components/catalog-monitor-pests";
import { CatalogItemVisitsComponent } from "./catalog-item-visits";
import { CatalogItemRentingComponent } from "./catalog-item-renting";
import { CatalogItemConsumablesComponent } from "./catalog-item-consumables";
import { CatalogItemBasePriceComponent } from "./catalog-item-baseprice";
import { CatalogItemImageComponent } from "./catalog-item-image";
import { CatalogItemTimesComponent } from "./catalog-item-times";
import { CatalogItemQpmServiceComponent } from "./catalog-item-qpm-service";
import { DialogTooltipButton } from "@/shared/components/dialog/dialog-tooltip-button";
import { MonitorAdditionalsEditor } from "@/routes/base-data/processes/qpm/monitors/[monitorId]/_components/monitor-additionals-editor";
import { useCatalogAdditionalLibraryItems } from "@/routes/base-data/processes/qpm/catalog/[catalogId]/[monitorId]/_components/catalog-monitor-additionals-library-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Label } from "@/shared/components/ui/label";
import { Input } from "@/shared/components/ui/input";
import { Checkbox } from "@/shared/components/ui/checkbox";
import {
  useQpmCatalogItemSetCapabilitiesMutation,
  useQpmCatalogItemSetCoreFieldsMutation,
  useQpmCatalogItemSetDetailsMutation,
} from "@/services/backend/qpm/catalog/api/catalog-api";
import type {
  CapabilitiesDTO,
  DetailsDTO,
  MonitorSnapDTO,
} from "@/services/backend/qpm/monitors/types/monitor-snapshot";
import { MonitorIoTClassUtil } from "@/services/backend/qpm/lib/monitor-iot-class-util";
import { EditDetailsComponent } from "@/routes/base-data/processes/qpm/monitors/[monitorId]/_components/edit-monitor-details";
import { useDebouncedDirtyMutationWithPersistenceStateContextUpdate } from "@/shared/lib/debounce/debounce";
import { usePersistenceStateUpdateOnTrigger } from "@/shared/lib/persistence-state/persistence-state-info";
import { usePersistenceContext } from "@/shared/lib/persistence-state/context";
import { PersistenceState } from "@/shared/lib/persistence-state/persistence-state";
import { MonitorDetailSection } from "@/routes/base-data/processes/qpm/monitors/[monitorId]/_components/monitor-detail-section";

function CatalogItemCoreFieldsComponent({
  catalogItemId,
  snapshot,
}: {
  catalogItemId: string;
  snapshot: MonitorSnapDTO;
}) {
  const [update, saveState] = useQpmCatalogItemSetCoreFieldsMutation();
  const [name, setName] = useState(snapshot.name);
  const [supplier, setSupplier] = useState(snapshot.supplier ?? "");

  useEffect(() => {
    setName(snapshot.name);
    setSupplier(snapshot.supplier ?? "");
  }, [snapshot.name, snapshot.supplier, catalogItemId]);

  const request = useMemo(
    () => ({
      catalogItemId,
      name,
      supplier,
    }),
    [catalogItemId, name, supplier],
  );

  const { onTrigger: persist } = usePersistenceStateUpdateOnTrigger(
    request,
    update,
    saveState.isLoading,
    saveState.error,
    saveState.isSuccess,
    saveState.reset,
    { toastError: true },
  );

  return (
    <>
      <div className="grid grid-cols-4 items-center gap-1.5">
        <Label className="col-span-2" htmlFor="catalog-item-name">
          {t("Name")}
        </Label>
        <Input
          className="col-span-2"
          id="catalog-item-name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          onBlur={persist}
        />
      </div>
      <div className="grid grid-cols-4 items-center gap-1.5">
        <Label className="col-span-2" htmlFor="catalog-item-supplier">
          {t("Lieferant")}
        </Label>
        <Input
          className="col-span-2"
          id="catalog-item-supplier"
          value={supplier}
          onChange={(event) => setSupplier(event.target.value)}
          onBlur={persist}
        />
      </div>
      <div className="grid grid-cols-4 items-center gap-1.5">
        <Label className="col-span-2" htmlFor="catalog-item-type">
          {t("Typ")}
        </Label>
        <Input
          className="col-span-2"
          id="catalog-item-type"
          value={MonitorTypeUtil.toLongString(snapshot.type)}
          disabled
        />
      </div>
    </>
  );
}

function CatalogItemIoTSpecComponent({
  catalogItemId,
  capabilities,
}: {
  catalogItemId: string;
  capabilities: CapabilitiesDTO;
}) {
  const [update, saveState] = useQpmCatalogItemSetCapabilitiesMutation();
  const iot = capabilities.iot;
  const iotClass = iot?.class;
  const [protocol, setProtocol] = useState(iot?.protocol ?? "");

  useEffect(() => {
    setProtocol(iot?.protocol ?? "");
  }, [iot?.protocol, catalogItemId]);

  const request = useMemo(
    () => ({
      catalogItemId,
      capabilities: {
        ...capabilities,
        iot: iotClass
          ? {
              protocol,
              class: iotClass,
            }
          : undefined,
      },
    }),
    [catalogItemId, capabilities, iotClass, protocol],
  );

  const { onTrigger: persist } = usePersistenceStateUpdateOnTrigger(
    request,
    update,
    saveState.isLoading,
    saveState.error,
    saveState.isSuccess,
    saveState.reset,
    { toastError: true },
  );

  if (!iot) return null;

  return (
    <>
      <div className="grid grid-cols-4 items-center gap-1.5">
        <Label className="col-span-2" htmlFor="catalog-item-iot-class">
          {t("IoT Typ")}
        </Label>
        <Input
          className="col-span-2"
          id="catalog-item-iot-class"
          value={MonitorIoTClassUtil.toString(iot.class)}
          disabled
        />
      </div>
      <div className="grid grid-cols-4 items-center gap-1.5">
        <Label className="col-span-2" htmlFor="catalog-item-iot-protocol">
          {t("Protokoll")}
        </Label>
        <Input
          className="col-span-2"
          id="catalog-item-iot-protocol"
          value={protocol}
          onChange={(event) => setProtocol(event.target.value)}
          onBlur={persist}
        />
      </div>
    </>
  );
}

function CatalogItemCapabilitiesComponent({
  catalogItemId,
  monitorType,
  capabilities,
}: {
  catalogItemId: string;
  monitorType: MonitorType;
  capabilities: CapabilitiesDTO;
}) {
  const [update] = useQpmCatalogItemSetCapabilitiesMutation();
  const { setPersistenceState } = usePersistenceContext();
  const isCheckpoint = monitorType === MonitorType.Checkpoint;

  const onChange = (field: keyof CapabilitiesDTO, value: boolean) => {
    setPersistenceState(PersistenceState.SAVING);
    update({
      catalogItemId,
      capabilities: {
        ...capabilities,
        [field]: value,
      },
    })
      .unwrap()
      .then(() => setPersistenceState(PersistenceState.SAVED))
      .catch(() => setPersistenceState(PersistenceState.ERROR));
  };

  return (
    <div className="flex flex-col gap-2">
      {isCheckpoint ? (
        <>
          <div className="border-border bg-muted/30 flex items-center gap-3 rounded-md border px-3 py-2">
            <Checkbox
              className="size-5"
              checked={capabilities.hasLethalAgent}
              onCheckedChange={(value) =>
                onChange("hasLethalAgent", value === true)
              }
            />
            <Label className="text-sm font-medium">
              {t("Toxischer Wirkstoff")}
            </Label>
          </div>
          <div className="border-border bg-muted/30 flex items-center gap-3 rounded-md border px-3 py-2">
            <Checkbox
              className="size-5"
              checked={capabilities.permanentBaiting}
              onCheckedChange={(value) =>
                onChange("permanentBaiting", value === true)
              }
            />
            <Label className="text-sm font-medium">
              {t("Dauerbeköderung")}
            </Label>
          </div>
          <div className="border-border bg-muted/30 flex items-center gap-3 rounded-md border px-3 py-2">
            <Checkbox
              className="size-5"
              checked={capabilities.checkpointVisual}
              onCheckedChange={(value) =>
                onChange("checkpointVisual", value === true)
              }
            />
            <Label className="text-sm font-medium">
              {t("Ausschließlich Visueller Prüfpunkt")}
            </Label>
          </div>
        </>
      ) : null}
    </div>
  );
}

function detailsEqual(left: DetailsDTO, right: DetailsDTO): boolean {
  return (
    left.description === right.description &&
    left.installationType === right.installationType &&
    left.heightMM === right.heightMM &&
    left.widthMM === right.widthMM &&
    left.depthMM === right.depthMM &&
    left.weightG === right.weightG &&
    left.coverageValue === right.coverageValue &&
    left.coveragePreText === right.coveragePreText &&
    left.coveragePostText === right.coveragePostText &&
    left.starterSpecification === right.starterSpecification &&
    left.protectionLevel === right.protectionLevel
  );
}

function CatalogItemDetailsComponent({
  catalogItemId,
  snapshot,
}: {
  catalogItemId: string;
  snapshot: MonitorSnapDTO;
}) {
  const [update, saveState] = useQpmCatalogItemSetDetailsMutation();
  const externalDetails = useMemo(() => snapshot.details, [snapshot.details]);
  const [syncedDetails, setSyncedDetails] = useState(externalDetails);
  const [draftDetails, setDraftDetails] = useState(externalDetails);

  useEffect(() => {
    setDraftDetails((current) =>
      detailsEqual(current, syncedDetails) ? externalDetails : current,
    );
    setSyncedDetails(externalDetails);
  }, [externalDetails, syncedDetails]);

  const request = useMemo(
    () => ({
      catalogItemId,
      details: draftDetails,
    }),
    [catalogItemId, draftDetails],
  );

  const persistDetails = useCallback(
    (nextRequest: typeof request) => update(nextRequest),
    [update],
  );

  useDebouncedDirtyMutationWithPersistenceStateContextUpdate(
    request,
    persistDetails,
    saveState.isLoading,
    saveState.error,
    saveState.isSuccess,
    saveState.reset,
    500,
    {
      toastError: true,
      baselineKey: catalogItemId,
      isDirty: (current, baseline) =>
        current.catalogItemId !== baseline.catalogItemId ||
        !detailsEqual(current.details, baseline.details),
    },
  );

  return (
    <EditDetailsComponent
      details={draftDetails}
      type={snapshot.type}
      onUpdate={(partial) =>
        setDraftDetails((current) => ({
          ...current,
          ...partial,
        }))
      }
    />
  );
}

export function CatalogMonitorComponent({
  baseMonitorId,
  catalogItem,
  prevNext,
  releaseId,
  catalogId,
}: {
  baseMonitorId: string;
  catalogItem: CatalogItemRes;
  monitorType: MonitorType;
  prevNext?: PrevNextNav;
  releaseId?: string;
  catalogId: string;
}) {
  const snap = catalogItem.snapshot;
  const effectiveMonitorType = snap.type;
  const { catalogAdditionalItems } = useCatalogAdditionalLibraryItems({
    catalogId,
  });

  const currentIndex = prevNext?.currentIndex ?? -1;
  const hasPrev = prevNext ? currentIndex > 0 : false;
  const hasNext = prevNext
    ? currentIndex >= 0 && currentIndex < prevNext.total - 1
    : false;
  const [refreshDialogOpen, setRefreshDialogOpen] = useState(false);
  const supportsQpmServiceToggle =
    effectiveMonitorType === MonitorType.Checkpoint ||
    effectiveMonitorType === MonitorType.FlyExterminator ||
    effectiveMonitorType === MonitorType.IoT;

  return (
    <>
      <CardHeader className="relative" key={baseMonitorId}>
        <div className="flex items-center justify-between gap-2">
          <CardTitle>
            {MonitorTypeUtil.toLongString(effectiveMonitorType)} {t("Details")}
          </CardTitle>

          {/* keep the exact same nav UI as MonitorComponent; just disable when not available */}
          <div className="flex items-center gap-2">
            <DialogTooltipButton
              open={refreshDialogOpen}
              onOpenChange={setRefreshDialogOpen}
              tooltip={t("Mit Basis Monitor aktualisieren")}
              dialogTitle={t("Mit Basis Monitor aktualisieren")}
              icon={<RefreshCcw className="h-4 w-4" />}
              buttonProps={{ className: "w-fit" }}
              dialogContentProps={{
                className: "max-h-[85vh] min-w-[80vw] overflow-y-scroll",
              }}
              content={
                <div className="min-w-0">
                  <CatalogReleaseRefreshControls
                    releaseId={releaseId}
                    monitorId={baseMonitorId}
                    catalogId={catalogId}
                    currentMonitorSnap={snap}
                    onApplied={() => setRefreshDialogOpen(false)}
                  />
                </div>
              }
            />

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={prevNext?.goPrev}
                    disabled={!prevNext || !hasPrev}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("Vorheriger")}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={prevNext?.goNext}
                    disabled={!prevNext || !hasNext}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("Nächster")}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </CardHeader>

      <CardContent key={`${baseMonitorId}-content`}>
        <div className="space-y-6">
          <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_420px]">
            <div className="space-y-6">
              <MonitorDetailSection title={t("Stammdaten")}>
                <div className="grid gap-3 lg:grid-cols-2">
                  <CatalogItemCoreFieldsComponent
                    key={`core-${catalogItem.catalogItemId}`}
                    catalogItemId={catalogItem.catalogItemId}
                    snapshot={snap}
                  />
                  <CatalogItemIoTSpecComponent
                    key={`iot-${catalogItem.catalogItemId}`}
                    catalogItemId={catalogItem.catalogItemId}
                    capabilities={snap.capabilities}
                  />
                </div>
              </MonitorDetailSection>

              <MonitorDetailSection title={t("Einstellungen")}>
                <div className="space-y-3">
                  <CatalogItemCapabilitiesComponent
                    key={`caps-${catalogItem.catalogItemId}`}
                    catalogItemId={catalogItem.catalogItemId}
                    monitorType={effectiveMonitorType}
                    capabilities={snap.capabilities}
                  />

                  {supportsQpmServiceToggle ? (
                    <CatalogItemQpmServiceComponent
                      catalogItemId={catalogItem.catalogItemId}
                      includeInCheckpointService={
                        snap.capabilities.includeInCheckpointService
                      }
                    />
                  ) : null}
                </div>
              </MonitorDetailSection>
            </div>

            <MonitorDetailSection title={t("Bild")}>
              <CatalogItemImageComponent
                key={`img-${baseMonitorId}`}
                catalogItemId={catalogItem.catalogItemId}
                image={snap.image}
                className="w-full"
              />
            </MonitorDetailSection>
          </div>

          <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_420px]">
            <div className="space-y-6">
              <MonitorDetailSection title={t("Material und Kalkulation")}>
                <div className="space-y-3">
                  <CatalogItemVisitsComponent
                    catalogItemId={catalogItem.catalogItemId}
                    value={snap?.recommendedVisits ?? 0}
                  />
                  <CatalogItemTimesComponent
                    catalogItemId={catalogItem.catalogItemId}
                    install={snap?.times?.install ?? 0}
                    maintain={snap?.times?.maintain ?? 0}
                  />
                  <CatalogItemBasePriceComponent
                    key={`price-${catalogItem.catalogItemId}`}
                    catalogItemId={catalogItem.catalogItemId}
                    priceEuros={snap?.basePrice?.price ?? 0}
                  />
                  <MonitorAdditionalsEditor
                    mode={{
                      kind: "catalog",
                      catalogItemId: catalogItem.catalogItemId,
                    }}
                    monitorName={snap.name}
                    monitorType={snap.type}
                    additionals={snap.additionals ?? []}
                    libraryItems={catalogAdditionalItems}
                  />
                </div>
              </MonitorDetailSection>

              <MonitorDetailSection title={t("Beschreibung und Katalogtext")}>
                <CatalogItemDetailsComponent
                  key={`details-${catalogItem.catalogItemId}`}
                  catalogItemId={catalogItem.catalogItemId}
                  snapshot={snap}
                />
              </MonitorDetailSection>
            </div>

            <div className="space-y-6">
              {MonitorTypeUtil.supportsRentingDefaults(effectiveMonitorType) ? (
                <MonitorDetailSection title={t("Konditionen")}>
                  <CatalogItemRentingComponent
                    catalogItemId={catalogItem.catalogItemId}
                    renting={snap?.rentingDefaults}
                  />
                </MonitorDetailSection>
              ) : null}
            </div>
          </div>

          <MonitorDetailSection title={t("Schädlingszuordnung")}>
            <div className="space-y-6">
              <CatalogMonitorPestsComponent
                key={`pests-${baseMonitorId}`}
                catalogItemId={catalogItem.catalogItemId}
                monitor={snap}
              />

              {effectiveMonitorType === MonitorType.FlyExterminator ? (
                <CatalogItemConsumablesComponent
                  catalogItemId={catalogItem.catalogItemId}
                />
              ) : null}
            </div>
          </MonitorDetailSection>
        </div>
      </CardContent>
    </>
  );
}
